import React from "react";
import { stageOf, money } from "@/hooks/useCalendarJobs";

export const PaidBadge = ({ paid, draft, id }) => (
  draft
    ? <span data-testid={`cal-paid-${id}`} data-paid="draft" className="text-[9px] font-bold uppercase tracking-wide rounded px-1 py-px bg-slate-500/20 text-slate-300">Draft</span>
    : <span data-testid={`cal-paid-${id}`} data-paid={paid} className={`text-[9px] font-bold uppercase tracking-wide rounded px-1 py-px ${paid ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>{paid ? "Paid" : "Unpaid"}</span>
);

// Compact job card: plate + make, final amount, paid/unpaid, stage. Draggable between dates.
export function JobCard({ job, config, onOpen, onStage, compact = false }) {
  const st = stageOf(config, job.stage);
  const onDragStart = (e) => { e.dataTransfer.setData("text/job", JSON.stringify(job)); e.dataTransfer.effectAllowed = "move"; };
  return (
    <div draggable onDragStart={onDragStart} onClick={() => onOpen(job)} data-testid={`cal-job-${job.id}`} data-draft={job.draft || undefined} data-stage={st.key}
      title={`${job.plate} · ${job.vehicle || ""} · ${st.name}`}
      className={`group relative rounded-md border border-slate-700/80 bg-slate-900/80 hover:bg-slate-800 cursor-pointer select-none overflow-hidden ${job.draft ? "border-dashed bg-slate-900/40" : ""} ${compact ? "px-1.5 py-1" : "p-2"}`}
      style={{ borderLeft: `3px solid ${st.colour}` }}>
      <div className="flex items-center justify-between gap-1">
        <span className="font-mono-num font-bold text-[12px] tracking-wide text-slate-100 truncate">{job.plate || job.quote_no}</span>
        <PaidBadge paid={job.paid} draft={job.draft} id={job.id} />
      </div>
      <div className="text-[11px] text-slate-300 truncate">{job.vehicle || "Vehicle"}</div>
      <div className="flex items-center justify-between gap-1 mt-0.5">
        {job.draft ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold truncate" style={{ color: st.colour }} data-testid={`cal-stage-${job.id}`}><span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: st.colour }} /> Quote · approve to start</span>
        ) : (
        <button type="button" onClick={(e) => { e.stopPropagation(); onStage(job); }} data-testid={`cal-stage-${job.id}`}
          className="inline-flex items-center gap-1 text-[10px] font-semibold rounded px-1 -mx-1 hover:bg-slate-700/70 truncate" style={{ color: st.colour }}>
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: st.colour }} /> {st.name.split(" ")[0]} ▾
        </button>
        )}
        <span className="text-[11px] font-mono-num font-bold text-amber-300 shrink-0" data-testid={`cal-amount-${job.id}`}>{money(job.total)}</span>
      </div>
    </div>
  );
}

// Stage picker: Ongoing ↔ Completed & Collected
export function StageMenu({ job, config, onPick, onClose }) {
  if (!job) return null;
  return (
    <div className="fixed inset-0 z-[60]" onClick={onClose} data-testid="cal-stage-menu">
      <div className="absolute left-1/2 top-24 -translate-x-1/2 w-72 rounded-xl border border-slate-700 bg-[#151D2E] shadow-2xl p-2" onClick={(e) => e.stopPropagation()}>
        <div className="px-2 py-1 text-xs text-slate-400">Set stage for <span className="font-mono-num text-slate-100">{job.plate}</span></div>
        {(config.job_stages || []).map((s) => (
          <button key={s.key} onClick={() => onPick(job, s.key)} data-testid={`cal-stage-opt-${s.key}`} data-on={job.stage === s.key}
            className="w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm text-left text-slate-200 hover:bg-slate-800 data-[on=true]:bg-slate-800">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.colour }} /> {s.name}
          </button>
        ))}
      </div>
    </div>
  );
}
